<template>
  <div class="modal fade" id="employee_email_import_modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Update Employee Emails from CSV</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="step === 'upload'" class="dq-import-upload">
            <p class="text-muted mb-3">
              Upload a CSV to update emails for existing employees. Match employees by
              <strong>PayrollNumber</strong>. Blank email cells are skipped.
            </p>

            <div class="alert alert-light border mb-3">
              <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <div class="fw-semibold mb-1">Need a template?</div>
                  <div class="fs-13 text-muted">
                    Download your current employees, fill in the Email column, then upload.
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-white"
                  :disabled="downloadingTemplate"
                  @click="downloadTemplate"
                >
                  <i class="ti ti-download me-1"></i>
                  {{ downloadingTemplate ? "Downloading..." : "Download Template" }}
                </button>
              </div>
            </div>

            <label class="form-label">CSV file</label>
            <input
              ref="fileInputRef"
              type="file"
              class="form-control"
              accept=".csv,text/csv"
              @change="onFileChange"
            />
            <div v-if="selectedFile" class="small text-muted mt-2">
              Selected: {{ selectedFile.name }}
            </div>
          </div>

          <div v-else>
            <div class="row g-3 mb-3">
              <div class="col-md-3 col-sm-6">
                <div class="border rounded p-3 h-100">
                  <div class="text-muted fs-12">Will update</div>
                  <div class="fs-18 fw-semibold text-success">{{ summary.will_update || 0 }}</div>
                </div>
              </div>
              <div class="col-md-3 col-sm-6">
                <div class="border rounded p-3 h-100">
                  <div class="text-muted fs-12">Skipped (blank)</div>
                  <div class="fs-18 fw-semibold">{{ summary.skipped_empty || 0 }}</div>
                </div>
              </div>
              <div class="col-md-3 col-sm-6">
                <div class="border rounded p-3 h-100">
                  <div class="text-muted fs-12">Unchanged</div>
                  <div class="fs-18 fw-semibold">{{ summary.skipped_unchanged || 0 }}</div>
                </div>
              </div>
              <div class="col-md-3 col-sm-6">
                <div class="border rounded p-3 h-100">
                  <div class="text-muted fs-12">Errors</div>
                  <div class="fs-18 fw-semibold text-danger">{{ summary.errors || 0 }}</div>
                </div>
              </div>
            </div>

            <div class="table-responsive border rounded">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Payroll No.</th>
                    <th>Employee</th>
                    <th>Current Email</th>
                    <th>New Email</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in previewRows" :key="`${row.line}-${row.payroll_number}`">
                    <td>{{ row.line }}</td>
                    <td>{{ row.payroll_number }}</td>
                    <td>{{ row.employee_name }}</td>
                    <td>{{ row.current_email || "—" }}</td>
                    <td>{{ row.new_email || "—" }}</td>
                    <td>
                      <span class="badge" :class="statusClass(row.status)">
                        {{ statusLabel(row.status) }}
                      </span>
                      <div class="small text-muted mt-1">{{ row.message }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal" @click="resetState">
            Cancel
          </button>

          <button
            v-if="step === 'preview'"
            type="button"
            class="btn btn-white"
            :disabled="loading"
            @click="backToUpload"
          >
            Back
          </button>

          <button
            v-if="step === 'upload'"
            type="button"
            class="btn btn-primary"
            :disabled="!selectedFile || loading"
            @click="runPreview"
          >
            <span v-if="loading">Previewing...</span>
            <span v-else>Preview Changes</span>
          </button>

          <button
            v-else
            type="button"
            class="btn btn-primary"
            :disabled="loading || (summary.will_update || 0) === 0"
            @click="runApply"
          >
            <span v-if="loading">Applying...</span>
            <span v-else>Apply {{ summary.will_update || 0 }} Update(s)</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import employeeService from "@/services/hrm/employeeService";
import { toast } from "@/utils/toast";

const emit = defineEmits(["imported"]);

const step = ref("upload");
const loading = ref(false);
const downloadingTemplate = ref(false);
const selectedFile = ref(null);
const previewRows = ref([]);
const summary = ref({});
const fileInputRef = ref(null);

function onFileChange(event) {
  const file = event.target.files?.[0] || null;
  selectedFile.value = file;
}

function resetState() {
  step.value = "upload";
  loading.value = false;
  selectedFile.value = null;
  previewRows.value = [];
  summary.value = {};
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
}

function backToUpload() {
  step.value = "upload";
  previewRows.value = [];
  summary.value = {};
}

async function downloadTemplate() {
  downloadingTemplate.value = true;
  try {
    const response = await employeeService.downloadEmailImportTemplate();
    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `employee-email-import-template-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to download template.", "error");
  } finally {
    downloadingTemplate.value = false;
  }
}

async function runPreview() {
  if (!selectedFile.value) return;

  loading.value = true;
  try {
    const { data } = await employeeService.previewEmailImport(selectedFile.value);
    previewRows.value = Array.isArray(data?.rows) ? data.rows : [];
    summary.value = data?.summary || {};
    step.value = "preview";
  } catch (e) {
    const message =
      e?.response?.data?.errors?.file?.[0] ||
      e?.response?.data?.message ||
      "Failed to preview CSV.";
    toast(message, "error");
  } finally {
    loading.value = false;
  }
}

async function runApply() {
  if (!selectedFile.value) return;

  loading.value = true;
  try {
    const { data } = await employeeService.applyEmailImport(selectedFile.value);
    toast(data?.message || "Employee emails updated.", "success");
    emit("imported");
    resetState();
    const modalEl = document.getElementById("employee_email_import_modal");
    if (modalEl && window.bootstrap?.Modal) {
      const instance =
        window.bootstrap.Modal.getInstance(modalEl) ||
        window.bootstrap.Modal.getOrCreateInstance(modalEl);
      instance.hide();
    }
  } catch (e) {
    const message =
      e?.response?.data?.errors?.file?.[0] ||
      e?.response?.data?.message ||
      "Failed to apply email updates.";
    toast(message, "error");
  } finally {
    loading.value = false;
  }
}

function statusClass(status) {
  switch (status) {
    case "update":
      return "badge-soft-success";
    case "skip_empty":
    case "skip_unchanged":
      return "badge-soft-secondary";
    default:
      return "badge-soft-danger";
  }
}

function statusLabel(status) {
  switch (status) {
    case "update":
      return "Will update";
    case "skip_empty":
      return "Skipped";
    case "skip_unchanged":
      return "Unchanged";
    default:
      return "Error";
  }
}

defineExpose({ resetState });
</script>
